import React, { useState, useEffect } from 'react';
import { 
  PageContainer,
  SectionGrid,
  Section,
  CardGrid,
  Card,
  CardContent,
  CardText,
  CardButton,
 } from '../styles';
import { getUserBooking, getUserCoupons, deleteUserBooking } from '../api';
import { Spinner, Collapsible } from '../components';

const Welcome = () => {
  const [userAppointments, setUserAppointments] = useState([]);
  const [coupons, setCoupons] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [message, setMessage] = useState(null);  // Estado para exibir mensagens de erro/sucesso

  const cancelBooking = async (id_booking) => {
    try {
      const response = await deleteUserBooking(id_booking);

      const data = await response.json();

      if (response.ok) {
        setMessage('Marcação cancelada com sucesso!');  // Exibe mensagem de sucesso
        setUserAppointments(userAppointments.filter(({id}) => id !== id_booking));  // Atualiza os agendamentos após cancelamento

      } else {
        setError(data.message || 'Erro ao cancelar a marcação.');  // Exibe mensagem de erro
      }

    } catch (err) {
      setError('Erro ao conectar com a API ao cancelar a marcação.');
    }
  };

  function formatCouponValue(coupon) {
    if (coupon.type === 'percentage') {
      return `${parseInt(coupon.value, 10)}%`;
    }

    return `R$ ${coupon.value}`;
  }

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const responseBookings = await getUserBooking();

        const dataBookings = await responseBookings.json();

        if (responseBookings.ok) {
          const formattedAppointments = Object.entries(dataBookings.userBookings || {}).flatMap(([date, bookings]) => 
            bookings.map((booking) => ({
              id: booking.bookingId,
              date: date, // Pegando a data da chave
              time: `${booking.startTime} - ${booking.endTime}`,
              type: booking.type === 'fixed' ? 'Mensal' : 'Avulsa',
              guests: booking.invitedUsers.length > 1 ? 'Convidados' : 'Convidado',
              guestNames: booking.invitedUsers.join(', ') || 'Nenhum convidado',
              status: booking.status,
              priorityExpiration: booking.priorityExpiration || null,
              canBeCancelled: booking.canBeCancelled,
            }))
          );

          setUserAppointments(formattedAppointments);

        } else {
          setError(dataBookings.message || 'Erro ao buscar agendamentos.');
        }

        const responseCoupons = await getUserCoupons();

        const dataCoupons = await responseCoupons.json();

        if (responseCoupons.ok) {
          const formattedCoupons = [
            ...(dataCoupons.userCoupons.validCoupons || []).map(coupon => ({ ...coupon, isValid: true })),
            //...(data.userCoupons.expiredCoupons || []).map(coupon => ({ ...coupon, isValid: false })),
          ];

          setCoupons(formattedCoupons);

        } else {
          setError(dataCoupons.message || 'Erro ao buscar cupons.');
        }

      } catch (err) {
        setError('Erro ao conectar com a API.');
      } finally {
        setLoading(false);
      }
    };

    fetchUserData();
  }, []);

  function formatDate(date) {
    const dateParts = date.split('-'); // Divide a data no formato aaaa-mm-dd

    if (dateParts.length !== 3) return ''; // Verifica se a data tem o formato correto (3 partes)

    const [year, month, day] = dateParts; // Desestrutura os componentes da data

    return `${day}/${month}/${year}`; // Retorna a data no formato dd/mm/aaaa
  }

  if (loading) {
    return <Spinner />;
  }

  return (
    <PageContainer>
      <h1 style={{fontFamily: "Bellota", color: "#3983C3"}}>Bem vindo(a) à Blue House Tennis!</h1>
      
      {/* Exibição da mensagem de sucesso ou erro */}
      {message && <CardText style={{ color: 'green' }}>{message}</CardText>}
      {error && <CardText style={{ color: 'red' }}>{error}</CardText>}

      <SectionGrid>
        <Collapsible title="Seus Agendamentos">
          <Section>
            {userAppointments.length === 0 ? (
              <CardText>Você não possui nenhum agendamento.</CardText>
            ) : (
              <CardGrid>
                {userAppointments.map((appointment) => (
                  <Card key={appointment.id}>
                    <CardContent>
                      <CardText><strong>Data:</strong> {appointment.date ? formatDate(appointment.date) : 'Data não disponível'}</CardText>
                      <CardText><strong>Horário:</strong> {appointment.time}</CardText>
                      <CardText><strong>Tipo:</strong> {appointment.type}</CardText>
                      <CardText><strong>{appointment.guests}:</strong> {appointment.guestNames}</CardText>
                      <CardText><strong>Status:</strong> {appointment.status}</CardText>
                    </CardContent>
                    {appointment.canBeCancelled && (
                      <CardButton onClick={() => cancelBooking(appointment.id)}>Desmarcar</CardButton>
                    )}
                  </Card>
                ))}
              </CardGrid>
            )}
          </Section>
        </Collapsible>
        
        <Collapsible title="Cupons Disponíveis">
          <Section>
            {coupons.length === 0 ? (
              <CardText>Você não possui nenhum cupom.</CardText>
            ) : (
              <CardGrid>
                {coupons.map((coupon) => (
                  <Card
                    key={coupon.coupon_code}
                    style={{ border: coupon.isValid ? '2px solid green' : '2px solid red' }}
                  >
                    <CardContent>
                      <CardText style={{ color: coupon.isValid ? 'green' : 'red' }}>
                        <strong>{coupon.isValid ? 'Cupom Válido' : 'Cupom Expirado'}</strong>
                      </CardText>
                      <CardText><strong>Código:</strong> {coupon.coupon_code}</CardText>
                      <CardText><strong>Desconto: </strong>{formatCouponValue(coupon)}</CardText>
                      <CardText>
                        <strong>Criado em:</strong> {formatDate(coupon.created_at)}
                      </CardText>
                      <CardText>
                        <strong>Expiração:</strong> {formatDate(coupon.expiration)}
                      </CardText>
                    </CardContent>
                  </Card>
                ))}
              </CardGrid>
            )}
          </Section>
        </Collapsible>
      </SectionGrid>
    </PageContainer>
  );
};

export default Welcome;
